import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is pay-as-you-go help desk software?',
    a: 'Pay-as-you-go help desk software charges for actual usage instead of per-seat subscriptions. Kora, for example, runs on credits: $1 buys 10 credits, creating a ticket costs 1 credit (10 cents), and an AI Analyst answer costs 1 credit. Collaboration features — members, comments, attachments, the Public API — are free. You top up a prepaid wallet from $5 and only spend when work happens.',
  },
  {
    q: 'How much does pay-as-you-go help desk software cost?',
    a: 'With Kora, a typical 10-agent team doing 300 tickets per month with 100 AI answers pays roughly $40/month. The same team on per-seat help desk software pays $190–$1,150/month (Freshdesk Growth to Zendesk Suite Professional). Light months cost less — there is no fixed monthly bill at all.',
  },
  {
    q: 'Is pay-as-you-go cheaper than per-agent pricing?',
    a: 'For most small and mid-size teams, yes. Per-agent pricing charges $19–115 per agent every month whether tickets arrive or not. Pay-as-you-go charges about 10 cents per ticket. A 5-agent team doing 100 tickets/month pays ~$10 on Kora versus $95–575 on per-seat plans. Very high-volume teams (1,000+ tickets/day) should compare carefully — usage pricing scales with volume, per-seat does not.',
  },
  {
    q: 'Do credits expire?',
    a: 'Kora credits purchased in packs are valid for 10 years. New workspaces also get 1,000 bonus credits valid for 30 days plus 100 base credits valid for 10 years — no credit card required to claim them.',
  },
  {
    q: 'What happens if my credit balance runs out?',
    a: 'Your data is never blocked — tickets, comments, and history stay fully readable. Kora lets the wallet overdraw slightly (down to −10 credits) so inbound requests keep flowing, then pauses new ticket creation and AI answers until you top up.',
  },
  {
    q: 'Which help desk tools offer pay-as-you-go pricing?',
    a: 'Kora is a pay-as-you-go help desk: credits meter ticket creation, AI Analyst answers and API responses (1 credit per 400) while seats and comments stay free. Intercom charges $0.99 per Fin AI outcome on top of per-seat pricing (intercom.com/pricing, checked 2026-10-21); Zendesk and Help Scout also sell AI resolutions on top of seats; most helpdesks remain per-seat.',
  },
];

const COMPARISON_ROWS = [
  { label: 'Entry price', kora: '$0 — 1,100 free credits, no card', typical: '$0 trial, then $15–55/agent/mo' },
  { label: 'Paying unit', kora: 'Per ticket (1 credit = $0.10)', typical: 'Per agent per month ($15–115)' },
  { label: 'Team of 10, 300 tickets/mo', kora: '~$30–40/mo', typical: '$190–$1,150/mo' },
  { label: 'Idle months', kora: 'Cost approaches $0', typical: 'Full per-seat bill' },
  { label: 'AI usage', kora: '1 credit per AI answer (~$0.10)', typical: '$0.99–2.00 per AI resolution or per-seat add-on' },
  { label: 'Adding a teammate', kora: 'Free — members cost nothing', typical: '+$15–115/mo per seat' },
  { label: 'Commitment', kora: 'Prepaid wallet, credits last 10 years', typical: 'Monthly/annual subscription' },
];

export function PayAsYouGoHelpdeskPage() {
  useEffect(() => {
    const title = 'Pay-As-You-Go Help Desk — Pay Per Ticket, Not Per Seat';
    const description = 'How usage-based help desk pricing works: $1 = 10 credits, 1 credit per ticket, unlimited members. Compare pay-as-you-go with per-agent costs at your volume.';
    setDocumentMeta(title, description);
    setCanonical('/pay-as-you-go-helpdesk');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/pay-as-you-go-helpdesk', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Pricing', url: 'https://usekora.app/pricing' },
        { name: 'Pay-As-You-Go Help Desk', url: 'https://usekora.app/pay-as-you-go-helpdesk' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Pricing guide · 2026"
      title="Pay-as-you-go help desk software: pay per ticket, not per seat"
      subtitle="Per-agent pricing charges you for capacity — whether work happens or not. Usage-based pricing charges you for outcomes. Here is how credit-based help desks work, what they cost at real volumes, and when they beat per-seat subscriptions."
      answer={
        <>
          <strong>Pay-as-you-go help desk software bills per ticket instead of per agent.</strong> Kora
          runs on credits: $1 = 10 credits, a ticket costs 1 credit (10¢), an AI Analyst answer costs 1
          credit — and members and comments are free; Public API responses cost 1 credit per 400. A 10-agent team doing 300
          tickets/month pays roughly $40, versus $190–$1,150 on per-seat plans.
        </>
      }
      ctaTitle="Try the pay-per-ticket help desk"
      ctaSubtitle="1,100 free credits on signup. No card, no seats, no monthly bill."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mt-4 mb-3">
        Why per-agent pricing is broken
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        Traditional help desks price the <em>team</em>, not the <em>work</em>. Zendesk Suite Team runs
        $55 per agent per month; Freshdesk Growth is $19; Help Scout Standard is $25 (list prices checked
        October 2026). A ten-person support team
        therefore pays $550, $190, or $250 — <strong>before a single ticket arrives</strong>. Add one
        teammate and your bill jumps by a fixed amount, even if they spend the month on documentation.
        The bill is the same in your busiest month and your quietest.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        Per-seat pricing made sense when help desk vendors charged mainly for the software used by
        agents. It makes less sense now that AI does the clerical work and collaboration is
        table stakes — charging per seat means <strong>taxing team size</strong>, which is the one
        thing a healthy company wants to grow.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        How credit-based pricing works
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        Kora replaces plans and seats with a prepaid credit wallet:
      </p>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>$1 buys 10 credits.</strong> Packs start at $5; your first recharge earns +30% bonus credits.</li>
        <li><strong>1 credit per ticket created</strong> — from the dashboard, Public API, or any connected channel.</li>
        <li><strong>1 credit per AI Analyst answer</strong> — plain-English questions over your ticket data.</li>
        <li><strong>Everything else is free:</strong> unlimited members, comments, AI ticket structuring, SLA tracking, automation rules, webhooks, embedded sessions, CSV export. The 20+ endpoint Public API costs 1 credit per 400 responses.</li>
        <li><strong>Purchased credits last 10 years.</strong> New workspaces claim 1,000 bonus credits (30 days) + 100 base credits (10 years) free.</li>
        <li><strong>The wallet can overdraw to −10 credits</strong> — an empty balance never blocks your customers, only new outbound work until you top up.</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        Pay-as-you-go vs per-seat: the real numbers
      </h2>
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 my-6">
        <table className="w-full min-w-[560px] text-sm bg-white ">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 ">
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Scenario</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Kora (credits)</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Typical per-seat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 ">
            {COMPARISON_ROWS.map((row) => (
              <tr key={row.label}>
                <td className="px-4 py-3 font-medium text-zinc-900 ">{row.label}</td>
                <td className="px-4 py-3 text-zinc-600 ">{row.kora}</td>
                <td className="px-4 py-3 text-zinc-600 ">{row.typical}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-400 mb-6">
        Per-seat estimates use list prices checked in October 2026 (Freshdesk Growth $19, Zendesk Suite Team
        $55 and Suite Professional $115, annual billing). Kora cost = tickets × $0.10 + AI answers × $0.10.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        When pay-as-you-go is (and isn't) the right choice
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        Usage pricing wins when ticket volume is modest relative to team size — startups, internal IT,
        ops teams, and product teams that collaborate heavily but ticket lightly. It also wins when
        your volume swings month to month: seasonal businesses, launches, support rotations. You stop
        paying for idle capacity entirely.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        Very high-volume operations — think 1,000+ tickets every single day with a small team — should
        model both models honestly, because at extreme volumes per-credit costs can pass a per-seat
        bill. That is the honest trade: usage pricing is cheaper for most real teams most of the time,
        and it scales <em>with the work</em>, not the headcount.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        What to look for in a pay-as-you-go help desk
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>Free collaboration:</strong> if members cost credits, seat pricing sneaked back in.</li>
        <li><strong>Long credit validity:</strong> 10-year credits mean top-ups are an asset, not a subscription.</li>
        <li><strong>AI billed per outcome:</strong> pay per successful AI answer, never for failures.</li>
        <li><strong>A small overdraft floor:</strong> your customer intake should not break at midnight because the wallet hit zero.</li>
        <li><strong>Transparent ledger:</strong> every debit visible, with the balance after each action.</li>
      </ul>

      <p className="text-sm text-zinc-600 mb-6">
        Related: <Link to="/helpdesk-without-per-seat-pricing" className="underline text-zinc-900">helpdesk without per-seat pricing</Link>.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 border-y border-zinc-100 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 ">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
        <p className="text-sm text-zinc-700 leading-relaxed">
          Want the full breakdown? See <Link to="/pricing" className="font-medium text-blue-600 hover:underline">Kora's pricing</Link> or
          run your own numbers in the <Link to="/helpdesk-cost-calculator" className="font-medium text-blue-600 hover:underline">cost calculator</Link>, or
          compare against <Link to="/compare/kora-vs-freshdesk" className="font-medium text-blue-600 hover:underline">Freshdesk</Link>,{' '}
          <Link to="/compare/kora-vs-zendesk" className="font-medium text-blue-600 hover:underline">Zendesk</Link>, and{' '}
          <Link to="/compare/kora-vs-zoho-desk" className="font-medium text-blue-600 hover:underline">Zoho Desk</Link>.
        </p>
      </div>
    </ContentPage>
  );
}
